import { FC, useContext, useEffect, useRef, useState } from "react";
import Card from "../components/ui/Card";
import { Acceptance, IChangeRequest, IGraphUser, IGroup } from "../types/ApiTypes";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { GroupsContext } from "../context/GroupsContext";
import useApi from "../hooks/useApi";
import { toast } from "react-hot-toast";
import Loading from "../components/Loading";
import { AppContext } from "../context/AppContext";
import Textarea from "../components/ui/Textarea";
import Autocomplete, { onAutocompleteChangeHandler } from "../components/ui/Autocomplete";
import Button from "../components/ui/Button";
import Table from "../components/ui/Table";
import { humanAcceptanceState } from "../helpers/humanSpaceState";
import { FaInfoCircle } from "react-icons/fa";
import { AnimatePresence } from "framer-motion";
import Modal from "../components/ui/Modal";
import Select, { onSelectChangeHandler } from "../components/ui/Select";
import AlertMessage from "../components/ui/AlertMessage";
import handleToastError from "../helpers/handleToastError";
import useAuthSocket from "../hooks/useAuthSocket";

interface InputFields {
  description: string;
}

const changeRequestType = [
  { value: "PRIMARY", label: "Besitzer/-in" },
  { value: "SECONDARY", label: "Stellvertreter/-in" }
];

const OwnerChange: FC = () => {
  const mounted = useRef(false);
  const [space, setSpace] = useState<IGroup>();
  const { register, formState: { errors }, handleSubmit, setValue } = useForm<InputFields>();
  const { fetchSpaceById, createChangeRequest, fetchChangeRequestsForSpace } = useApi();
  const {socket} = useAuthSocket();

  const [spaceChangeRequests, setSpaceChangeRequests] = useState<IChangeRequest[]>([]);
  const [usersOptions, setUsersOptions] = useState<{ label: string, value: IGraphUser }[]>([]);
  const [selectedNewOwner, setSelectedNewOwner] = useState<{ label: string, value: IGraphUser }>();
  const [selectedChangeRequestType, setSelectedChangeRequestType] = useState<IChangeRequest["type"]>("SECONDARY");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<IChangeRequest>();

  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const groupCtx = useContext(GroupsContext);
  const appCtx = useContext(AppContext);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    socket.off('OWNER_STATUS_CHANGED');
    socket.on('OWNER_STATUS_CHANGED', (ownerChange: IChangeRequest) => {
      setSpaceChangeRequests(prev => {
        const selected = prev.filter(x => x.id !== ownerChange.id);
        return [
          ownerChange,
          ...selected
        ];
      })
    })
    return () => {
      socket.off('OWNER_STATUS_CHANGED');
    }
  }, [socket]);

  useEffect(() => {
    if (space) {
      fetchChangeRequestsForSpace(space.requestId).then(res => {
        if (res && mounted.current) setSpaceChangeRequests(res);
      });
    }
  }, [space, fetchChangeRequestsForSpace]);

  useEffect(() => {
    (async () => {
      if (groupCtx.state.groups.length === 0) return;
      if (params.id) {
        let space = groupCtx.state.groups.find(x => x.requestId === params.id);
        if(!space) {
          space = await fetchSpaceById(params.id);
        }
        if (!space) {
          toast.error(`Space with id ${params.id} not found`);
          return navigate("/");
        }
        setSpace(space);
      }
    })();
  }, [params.id, groupCtx.state.groups]);

  useEffect(() => {
    if (space && appCtx.state.user.length > 0) {
      setUsersOptions(appCtx.state.user.map(x => ({ label: x.displayName, value: x })));
    }
  }, [space, appCtx.state.user]);

  useEffect(() => {
    console.log('repeating')
    if (selectedChangeRequest && appCtx.state.user.length > 0) {
      setSelectedChangeRequest(prev => {
        if (prev) {
          return {
            ...prev,
            new_owner: appCtx.state.user.find(x => x.id === prev.new_owner)?.displayName || prev.new_owner,
            old_owner: appCtx.state.user.find(x => x.id === prev.old_owner)?.displayName || prev.old_owner,
            requester: appCtx.state.user.find(x => x.id === prev.requester)?.displayName || prev.requester
          };
        }
        return prev;
      });
    }
  }, [selectedChangeRequest?.id, appCtx.state.user]);

  const onNewOwnerChange: onAutocompleteChangeHandler<IGraphUser> = (selected) => {
    setSelectedNewOwner(selected);
  };

  const onChangeRequestTypeChange: onSelectChangeHandler<string> = (pick) => {
    console.log(pick.value);
    setSelectedChangeRequestType(pick.value as "PRIMARY" | "SECONDARY");
  };

  const onSubmit: SubmitHandler<InputFields> = (e) => {
    if (!selectedNewOwner) return;

    if (selectedNewOwner.value.id === space?.primary_owner) return toast.error("Der neue Besitzer hat sich nicht geändert");
    if (selectedNewOwner.value.id === space?.secondary_owner) return toast.error("Der neue Besitzer ist bereits der Stellvertreter");
    if (!space) return;

    return toast.promise(createChangeRequest({
      new_owner: selectedNewOwner.value.id,
      spaceId: space.requestId,
      reason: e.description,
      type: selectedChangeRequestType
    }), {
      loading: "Der Antrag wird erstellt",
      success: (data) => {
        setValue("description", "");
        setSelectedNewOwner(undefined);
        setSpaceChangeRequests(prev => [data, ...prev]);
        return <span className="text-sm">Der Antrag für den Bereich<br /><b>{space.type} {space.name}</b> wurde erstellt.</span>;
      },
      error: (err) => handleToastError(err, `Der Antrag für den Bereich ${space.type} ${space.name} konnte nicht erstellt werden`)
    });
  };

  return (
    space ? (
      <>
        <section className="grid grid-cols-1 lg:grid-cols-4 gap-y-4 lg:gap-x-4">
          <div>
            <Card title="Wechsele den Besitzer des Spaces">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="font-semibold" htmlFor="position">Welche Stelle soll ersetzt werden?</label>
                  <Select<string> options={changeRequestType} defaultValue={changeRequestType[1]}
                                  onChange={onChangeRequestTypeChange} />
                  {selectedChangeRequestType === "PRIMARY" && (
                    <div className="mt-2">
                      <AlertMessage severity="warning">Bitte denke daran, dass ein Besitzer-Wechsel nur in sehr seltenen
                        Fällen möglich ist, da die Besitzer hauptsächlich Team- oder Bereichsleiter sind, die für die
                        Spaces verantworten.</AlertMessage>
                    </div>
                  )}
                </div>
                <div>
                  <label className="font-semibold"
                         htmlFor="reason">Neuer {selectedChangeRequestType === "PRIMARY" ? "Besitzer" : "Stellvertreter"}</label>
                  <Autocomplete options={usersOptions}
                                defaultValue={usersOptions.find(x => x.value.id === space.primary_owner)}
                                onChange={onNewOwnerChange} />
                </div>
                <div>
                  <label className="font-semibold" htmlFor="description">Begründung</label>
                  <Textarea id="description" label="description" {...register("description", { minLength: 10 })} />
                  {errors.description &&
                    <span className="text-xs text-error-brighter">Begründung ist ein Pflichtfeld und muss mindestens 10 Zeichen lang sein.</span>}
                </div>
                <div className="w-full flex justify-end">
                  <Button type="submit">Antrag stellen</Button>
                </div>
              </form>
            </Card>
          </div>
          <div className="lg:col-span-3">
            <Card title="Vorherige Besitzer">
              <Table.Skeleton>
                <Table.THead>
                  <Table.HeadTR>
                    <Table.TH>Antragsdatum</Table.TH>
                    <Table.TH>Typ</Table.TH>
                    <Table.TH>Alter Besitzer</Table.TH>
                    <Table.TH>Neuer Besitzer</Table.TH>
                    <Table.TH>Antragsstatus</Table.TH>
                    <Table.TH />
                  </Table.HeadTR>
                </Table.THead>
                <Table.TBody>
                  {spaceChangeRequests.length > 0 ? spaceChangeRequests.map(x => (
                    <Table.BodyTR key={x.id}>
                      <Table.TD>{new Date(x.createdAt).toLocaleString()}</Table.TD>
                      <Table.TD>{x.type === "PRIMARY" ? "Besitzer" : "Stellvertretung"}</Table.TD>
                      <Table.TD>{x.old_owner}</Table.TD>
                      <Table.TD>{x.new_owner}</Table.TD>
                      <Table.TD>{humanAcceptanceState(x.request_state)}</Table.TD>
                      <Table.TD>
                                            <span className="flex items-center cursor-pointer" onClick={() => {
                                              setSelectedChangeRequest(x);
                                              setShowInfoModal(true);
                                            }}>
                                                <FaInfoCircle className="mr-1" /> Info
                                            </span>
                      </Table.TD>
                    </Table.BodyTR>
                  )) : (
                    <Table.BodyTR>
                      <Table.TD colspan={6}>Keine Anträge gefunden</Table.TD>
                    </Table.BodyTR>
                  )}
                </Table.TBody>
              </Table.Skeleton>
            </Card>
          </div>
        </section>
        <AnimatePresence>
          {showInfoModal && <Modal title="Details zum Antrag" isOpen={showInfoModal} onClose={() => {
            setSelectedChangeRequest(undefined);
            setShowInfoModal(false);
          }}>
            <div className="space-y-2">
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Status:</span> {humanAcceptanceState(selectedChangeRequest!.request_state)}
              </div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Neuer Besitzer:</span> {selectedChangeRequest!.new_owner}</div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Alter Besitzer:</span> {selectedChangeRequest!.old_owner}</div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Auftraggeber:</span> {selectedChangeRequest!.requester}</div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Begründung:</span><br />{selectedChangeRequest!.reason}</div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Space:</span> {selectedChangeRequest!.space!.type} {selectedChangeRequest!.space.name}
              </div>
              <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Erstellt am:</span> {new Date(selectedChangeRequest!.createdAt).toLocaleString()}
              </div>
              {selectedChangeRequest?.updatedAt && <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Letzte Bearbeitung am:</span> {new Date(selectedChangeRequest!.updatedAt).toLocaleString()}
              </div>}
              {selectedChangeRequest?.deletedAt && <div className="bg-black/10 rounded-md p-2"><span
                className="text-accentGray">Gelöscht am:</span> {new Date(selectedChangeRequest!.deletedAt).toLocaleString()}
              </div>}
              {selectedChangeRequest!.request_state === Acceptance.Declined && (
                <AlertMessage severity="info">
                  Die detaillierten Informationen können in den Logs gefunden werden
                </AlertMessage>
              )}
            </div>
          </Modal>}
        </AnimatePresence>
      </>
    ) : <Loading small />
  );
};

export default OwnerChange;